/**
 * ObserveStage — custom observation form per experiment.
 * User fills a table row-by-row; Brimi validates each row via /api/experiments/{id}/observe/validate.
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, AlertCircle, Loader2, ArrowRight, Beaker, Calculator as CalcIcon } from "lucide-react";
import Calculator from "@/components/Calculator";

/** Physics-aware formula chip suggestions per experiment. */
const FORMULA_CHIPS = {
  "phy-9-pendulum": [
    { label: "Mean T", template: "(t1+t2+t3)/60" },
    { label: "T²", template: "T^2" },
    { label: "g = 4π²L/T²", template: "4*π^2*(L/100)/T^2" },
    { label: "% error vs 9.8", template: "(g-9.8)/9.8*100" },
  ],
  "phy-9-circuit-bulbs": [
    { label: "V = IR", template: "I*R" },
    { label: "R_series", template: "R1+R2" },
    { label: "R_parallel", template: "(R1*R2)/(R1+R2)" },
    { label: "Power", template: "V^2/R" },
  ],
  "phy-11-parallelogram": [
    { label: "|R|", template: "√(P^2+Q^2+2*P*Q*cos(θ))" },
    { label: "tan α", template: "(Q*sin(θ))/(P+Q*cos(θ))" },
  ],
};

const chipsFor = (id) => FORMULA_CHIPS[id] || null;

export const ObserveStage = ({ experiment, observeCfg, onComplete }) => {
  const columns = observeCfg?.columns || [];
  const initialRows = observeCfg?.rows || [];
  const [rows, setRows] = useState(initialRows.map((r) => ({ ...r })));
  const [rowStatus, setRowStatus] = useState({}); // { idx: {status, hint, correct_T, correct_T2} }
  const [validatingIdx, setValidatingIdx] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);

  const updateCell = (rowIdx, key, val) => {
    setRows((rs) => {
      const copy = [...rs];
      copy[rowIdx] = { ...copy[rowIdx], [key]: val };
      return copy;
    });
  };

  const validateRow = async (rowIdx) => {
    setValidatingIdx(rowIdx);
    try {
      const { data } = await api.post(`/experiments/${experiment.id}/observe/validate`, { row: rows[rowIdx] });
      setRowStatus((s) => ({ ...s, [rowIdx]: data }));
    } catch (err) {
      console.error("Observation validation failed:", err);
      setRowStatus((s) => ({ ...s, [rowIdx]: { status: "err", hint: "Could not validate. Try again." } }));
    } finally {
      setValidatingIdx(null);
    }
  };

  const okRows = Object.values(rowStatus).filter((r) => r.status === "ok").length;
  const canProceed = okRows >= Math.min(3, rows.length);

  if (!columns.length) {
    return (
      <div className="p-8 text-slate-400 text-center">
        Observation schema not defined for this experiment yet.
        <Button className="mt-4 mx-auto block bg-cyan-500 text-slate-950 rounded-full" onClick={onComplete}>Continue to Report</Button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto overflow-x-hidden p-2 sm:p-4">
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl bg-slate-900/70 border border-cyan-500/30 p-4 sm:p-5 mb-4">
          <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
            <Beaker className="h-3.5 w-3.5" /> Observations
          </div>
          <p className="text-slate-200 leading-relaxed">{observeCfg?.instruction}</p>
          <p className="mt-2 text-xs text-slate-400 font-mono">
            {okRows} / {rows.length} rows verified · Brimi checks physics accuracy of your readings.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-slate-800/70">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="px-3 py-3 text-left text-cyan-300 font-mono text-xs uppercase tracking-wider">
                    {c.label}
                  </th>
                ))}
                <th className="px-3 py-3 text-left text-cyan-300 font-mono text-xs uppercase tracking-wider">Check</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => {
                const st = rowStatus[rowIdx];
                return (
                  <React.Fragment key={rowIdx}>
                    <tr className={`border-t border-slate-800 ${st?.status === "ok" ? "bg-emerald-950/40" : st?.status === "warn" ? "bg-amber-950/30" : ""}`}>
                      {columns.map((c) => {
                        const inputType = c.type === "text" ? "text" : "number";
                        const isReadonly = c.readonly === true || c.key === "L";
                        return (
                          <td key={c.key} className="px-3 py-2">
                            {isReadonly ? (
                              <div
                                className="h-9 flex items-center px-3 rounded-md bg-slate-800/50 border border-slate-700 text-cyan-300 font-mono text-sm"
                                data-testid={`observe-cell-${rowIdx}-${c.key}`}
                              >
                                {row[c.key] ?? "—"}
                              </div>
                            ) : (
                              <Input
                                type={inputType}
                                value={row[c.key] ?? ""}
                                onChange={(e) => updateCell(rowIdx, c.key, e.target.value)}
                                className="h-9 bg-slate-800 border-slate-700 text-slate-100 text-sm"
                                placeholder={c.hint || ""}
                                data-testid={`observe-cell-${rowIdx}-${c.key}`}
                              />
                            )}
                          </td>
                        );
                      })}
                      <td className="px-3 py-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => validateRow(rowIdx)}
                          disabled={validatingIdx === rowIdx}
                          className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 h-9 rounded-full"
                          data-testid={`observe-check-${rowIdx}`}
                        >
                          {validatingIdx === rowIdx ? <Loader2 className="h-4 w-4 animate-spin" /> :
                           st?.status === "ok" ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> :
                           st?.status ? <AlertCircle className="h-4 w-4 text-amber-400" /> :
                           "Check"}
                        </Button>
                      </td>
                    </tr>
                    {st && (
                      <tr>
                        <td colSpan={columns.length + 1} className={`px-4 py-2 text-xs border-t border-slate-800 ${st.status === "ok" ? "text-emerald-300 bg-emerald-950/20" : st.status === "warn" ? "text-amber-300 bg-amber-950/20" : "text-rose-300 bg-rose-950/20"}`}>
                          {st.status === "ok" ? "✓ " : "⚠ "}{st.hint}
                          {st.correct_T !== undefined && st.status !== "ok" && (
                            <span className="ml-2 text-slate-400">(Expected T ≈ {st.correct_T}s, T² ≈ {st.correct_T2} s²)</span>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-between items-center">
          <Button
            variant="outline"
            className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/20 rounded-full h-11 px-5"
            onClick={() => setCalcOpen(true)}
            data-testid="observe-open-calc"
          >
            <CalcIcon className="mr-2 h-4 w-4" /> Calculator
          </Button>
          <Button
            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full h-11 px-6"
            onClick={onComplete}
            disabled={!canProceed}
            data-testid="observe-goto-report"
          >
            {canProceed ? "Continue to Report" : `Verify at least ${Math.min(3, rows.length)} rows first`}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>

      <Calculator open={calcOpen} onClose={() => setCalcOpen(false)} formulas={chipsFor(experiment.id)} />
    </div>
  );
};

export default ObserveStage;
